import React, { forwardRef, useEffect, useState } from 'react'
import Calendar from 'react-calendar'
// import 'react-calendar/dist/Calendar.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { useOnClickOutside } from 'usehooks-ts'
import { useRef } from 'react'
// import { log } from '../../helpers/utils'

// Extend dayjs with plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
const defaultTimezone = 'America/New_York'
dayjs.tz.setDefault(defaultTimezone)

/**
 * Determines if a calendar tile should be disabled based on the business rules
 * from the provided table:
 *
 * Order Placed Period:                       | Earliest Mail Date:
 * ---------------------------------------------|-------------------
 * Monday 12:00am - Monday 11:59am            | Wednesday
 * Monday 12:00pm - Tuesday 11:59am           | Thursday
 * Tuesday 12:00pm - Wednesday 11:59am        | Friday
 * Wednesday 12:00pm - Thursday 11:59am       | Monday (next week)
 * Thursday 12:00pm - Sunday 11:59pm          | Tuesday
 *
 * IMPORTANT: Valid mail days include Monday (1), Tuesday (2), Wednesday (3), Thursday (4), and Friday (5).
 * Saturday (6) and Sunday (0) are NOT valid mail days.
 */
function tileDisabled({ date, view }) {
    // Only apply rules to month view
    if (view === 'month') {
        // Convert to a native JS Date for consistency
        const jsDate = new Date(date)
        const dateInTimezone = dayjs(jsDate).tz(defaultTimezone)
        const today = dayjs(new Date()).tz(defaultTimezone)

        // Get day of week number from native Date: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
        const dayNumber = jsDate.getDay()

        // Debug logging (uncomment if needed)
        // log('Checking date:', dateInTimezone.format('YYYY-MM-DD'), 'Day:', dayNumber, 'Name:', dateInTimezone.format('dddd'))

        // 1. Disable dates in the past
        if (dateInTimezone.isBefore(today, 'day')) {
            return true // DISABLED
        }

        // 2. ONLY allow Monday through Friday (1-5)
        // Weekend days (Saturday and Sunday) should be DISABLED
        const validDays = [1, 2, 3, 4, 5] // Monday through Friday
        if (!validDays.includes(dayNumber)) {
            return true // DISABLED - weekend days are not valid mail days
        }

        // 3. Apply earliest mail date rules based on current day/time
        const currentDay = today.day() // 0-6 (Sun-Sat)
        const currentHour = today.hour()
        const isBeforeNoon = currentHour < 12

        // Get next occurrence of a specific day of the week
        const getNextOccurrence = (targetDay) => {
            let daysToAdd = targetDay - currentDay
            if (daysToAdd <= 0) {
                daysToAdd += 7 // Go to next week
            }
            return today.add(daysToAdd, 'day').startOf('day')
        }

        let earliestValidMailDay

        // Implementation based on the business rules table
        // Case 1: Monday before noon → Wednesday
        if (currentDay === 1 && isBeforeNoon) {
            earliestValidMailDay = 3 // Wednesday
        }
        // Case 2: Monday after noon to Tuesday before noon → Thursday
        else if (
            (currentDay === 1 && !isBeforeNoon) ||
            (currentDay === 2 && isBeforeNoon)
        ) {
            earliestValidMailDay = 4 // Thursday
        }
        // Case 3: Tuesday after noon to Wednesday before noon → Friday
        else if (
            (currentDay === 2 && !isBeforeNoon) ||
            (currentDay === 3 && isBeforeNoon)
        ) {
            earliestValidMailDay = 5 // Friday
        }
        // Case 4: Wednesday after noon to Thursday before noon → Monday
        else if (
            (currentDay === 3 && !isBeforeNoon) ||
            (currentDay === 4 && isBeforeNoon)
        ) {
            earliestValidMailDay = 1 // Monday
        }
        // Case 5: Thursday after noon through Sunday → Tuesday
        else {
            earliestValidMailDay = 2 // Tuesday
        }

        // Get date of the next occurrence of the earliest valid mail day
        const earliestDate = getNextOccurrence(earliestValidMailDay)

        // Debug logging (uncomment if needed)
        // log('Current day:', currentDay, isBeforeNoon ? 'before noon' : 'after noon')
        // log('Earliest valid day:', earliestValidMailDay, '(' + ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][earliestValidMailDay] + ')')
        // log('Earliest date:', earliestDate.format('YYYY-MM-DD'))

        // If the date being checked is before the earliest valid date, disable it
        if (dateInTimezone.isBefore(earliestDate, 'day')) {
            return true // DISABLED - before earliest valid date
        }

        // If we've passed all checks, this date is valid
        return false // ENABLED
    }

    return false // ENABLED for non-month views
}

/**
 * DateInput component that displays a calendar for selecting valid mail dates
 * based on business rules.
 */
const DateInput = forwardRef(({ item, shouldReset, ...props }, ref) => {
    const [date, setDate] = useState(new Date())
    const [showCalendar, setShowCalendar] = useState(false)
    const [value, setValue] = useState('')

    const { setValue: parentSetValue } = props

    const divRef = useRef(null)
    useOnClickOutside(divRef, () => setShowCalendar(false))

    // Handle date selection
    function onChange(nextValue) {
        setDate(nextValue)
        setShowCalendar(false)
        setValue(dayjs(nextValue).format('MM-DD-YYYY'))
        parentSetValue(item.name, dayjs(nextValue).format('MM-DD-YYYY'))
    }

    // Reset when shouldReset changes
    useEffect(() => {
        setValue('')
        parentSetValue(item.name, null)
    }, [shouldReset])

    return (
        <div className="relative mb-[11px]" ref={divRef}>
            <label
                htmlFor="dateInput"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">
                Mailing Date
            </label>
            <div className="relative">
                <input
                    id="dateInput"
                    value={value}
                    readOnly
                    ref={ref}
                    type="text"
                    className="block w-full rounded-[10px] border border-[#E1DACE] bg-white px-3.5 py-[13px] pr-[42px] text-[14.5px] font-medium text-[#1A1D23] shadow-none transition-all duration-150 placeholder:text-gray-400 focus:border-[#F28230] focus:outline-none focus:ring-4 focus:ring-[#F28230]/15"
                    autoComplete="chrome-off"
                    placeholder="Select a mailing date"
                    onFocus={() => setShowCalendar(true)}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-500">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"
                        />
                    </svg>
                </div>
            </div>
            {showCalendar && (
                <div className="absolute z-10 mt-2 overflow-hidden rounded-[14px] border border-[#ECE6DC] bg-white shadow-[0_14px_36px_rgba(26,29,35,0.14)]">
                    <Calendar
                        onClickDay={onChange}
                        value={date}
                        tileDisabled={tileDisabled}
                    />
                </div>
            )}
        </div>
    )
})

DateInput.displayName = 'DateInput'

export { DateInput }
